import { useLazyQuery, useMutation } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { INVENTORY_HEALTH_QUERY } from '../../graphql/inventoryHealth'
import { PMS_TRIGGER_MUTATION } from '../../graphql/pmsTrigger'
import { getTranslate } from '../../localization'
import { InventoryHealthResponse } from '../../types/inventoryHealth'

/**
 * The server's maximum page. Asking for it once, rather than paging
 * server-side, is what lets the column filters be honest: they narrow
 * everything that was fetched rather than one page of many. Where the server
 * still truncated, the screen says so — a filter that silently searched a
 * fraction of the data would be worse than no filter.
 */
const FETCH_PER_PAGE = 2000

export type DiagnosticVariables = {
  propertyCode: string
  providerCode: string
  from: string
  to: string
  channelID?: string
  differencesOnly: boolean
}

/**
 * Data layer for the screen: the diagnostic read, the PMS trigger, and the
 * re-read that follows it. Lives outside index.tsx so that file composes and
 * does not also fetch (§8.4).
 */
/**
 * onReread fires when the post-trigger re-read runs. The re-read happens on a
 * timer INSIDE this hook, so anything else that must refresh with it — the PMS
 * evidence panel, whose whole purpose is answering "did the pull I just fired
 * land?" — has no other way to know. Without it the comparison refreshed while
 * the evidence beside it still showed the conversation from before the trigger.
 */
export function useInventoryHealthDiagnostic(
  onReread?: (propertyCode: string) => void
) {
  const [pendingRefresh, setPendingRefresh] = useState(false)
  const refreshTimer = useRef<number | undefined>(undefined)
  const lastVariables = useRef<DiagnosticVariables | null>(null)

  // A timeout that outlives the screen calls into a query that no longer has a
  // component to render into.
  useEffect(
    () => () => {
      if (refreshTimer.current !== undefined) {
        window.clearTimeout(refreshTimer.current)
      }
    },
    []
  )

  const [runQuery, { data, loading, error }] =
    useLazyQuery<InventoryHealthResponse>(INVENTORY_HEALTH_QUERY, {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: (e) => toast.error(e.message)
    })

  const run = useCallback(
    (variables: DiagnosticVariables) => {
      lastVariables.current = variables
      runQuery({
        variables: { input: { ...variables, page: 1, perPage: FETCH_PER_PAGE } }
      })
    },
    [runQuery]
  )

  const [triggerMutation, { loading: triggering }] = useMutation(
    PMS_TRIGGER_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        toast.success(
          getTranslate(
            'Import triggered. Re-running the diagnostic — the PMS may take a few seconds to respond.'
          )
        )
        // Re-read after the trigger so the operator sees before and after in
        // one place. The PMS call is asynchronous, so an unchanged result here
        // means the pull has not landed yet, not that it failed.
        //
        // pendingRefresh keeps the screen visibly busy for the whole wait —
        // otherwise both spinners stop and the screen looks finished for five
        // seconds while a re-read is still pending.
        setPendingRefresh(true)
        refreshTimer.current = window.setTimeout(() => {
          setPendingRefresh(false)
          if (lastVariables.current != null) {
            run(lastVariables.current)
            onReread?.(lastVariables.current.propertyCode)
          }
        }, 5000)
      },
      onError: (e) => toast.error(e.message)
    }
  )

  const triggerPull = useCallback(
    (propertyCode?: string, providerCode?: string) => {
      triggerMutation({ variables: { propertyCode, providerCode } })
    },
    [triggerMutation]
  )

  return {
    result: data?.inventoryHealthDiagnostic,
    loading,
    error,
    busy: loading || triggering || pendingRefresh,
    run,
    triggerPull
  }
}
